import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { RefreshCw, Target, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import ClusterCard from '../components/daily-targets/ClusterCard';
import RevenueTimelineChart from '../components/daily-targets/RevenueTimelineChart';

const DailyTargetDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchDashboard = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/daily-targets/dashboard?date=${date}`);
      setDashboardData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Daily target config not seeded yet. Ask admin to seed via /api/daily-targets/config/seed');
      } else {
        setError(err.response?.data?.detail || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard(selectedDate);
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchDashboard(selectedDate), 60000);
    return () => clearInterval(interval);
  }, [selectedDate, fetchDashboard]);

  const summary = dashboardData?.summary || {};
  const clusters = dashboardData?.clusters || [];
  const completionPct = summary.total_target > 0
    ? ((summary.total_done / summary.total_target) * 100).toFixed(1)
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Target className="w-7 h-7 text-blue-600" />
            Daily Target Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Cluster targets → gym drilldown → time slot breakdown
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <button
            onClick={() => fetchDashboard(selectedDate)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Last refresh */}
      {lastRefresh && (
        <p className="text-xs text-gray-400">
          Last updated: {format(lastRefresh, 'HH:mm:ss')} · Auto-refreshes every 60s
        </p>
      )}

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !dashboardData && (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {/* KPI Summary Cards */}
      {dashboardData && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 col-span-2 md:col-span-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Target</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
              ₹{Math.round(summary.total_target || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Revenue Done</p>
            <p className="text-xl font-bold text-green-600 mt-1">
              ₹{Math.round(summary.total_done || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Outstanding</p>
            <p className="text-xl font-bold text-orange-600 mt-1">
              ₹{Math.round(Math.max(0, (summary.total_target || 0) - (summary.total_done || 0))).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Completion</p>
            <p className={`text-xl font-bold mt-1 ${completionPct >= 80 ? 'text-green-600' : completionPct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
              {completionPct}%
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Staff Present</p>
            <p className="text-xl font-bold text-blue-600 mt-1">
              {summary.staff_present || 0}/{summary.staff_total || 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Gyms Reporting</p>
            <p className="text-xl font-bold text-purple-600 mt-1">
              {summary.gyms_reporting || 0}/{summary.gyms_total || 0}
            </p>
          </div>
        </div>
      )}

      {/* Cluster Cards */}
      {clusters.map((cluster) => (
        <ClusterCard key={cluster.tl_id} cluster={cluster} />
      ))}

      {/* Revenue vs Target Pace Chart */}
      {dashboardData && (
        <RevenueTimelineChart
          totalTarget={summary.total_target || 0}
          revenueDone={summary.total_done || 0}
          selectedDate={selectedDate}
          clusters={clusters}
        />
      )}
    </div>
  );
};

export default DailyTargetDashboard;
